/**
 * Sentences about a run: what one row is, who was considered, what was left out and how values were
 * worked out. Shared by the run page, printouts, CSV headers and the agent so they always agree.
 */
import type { Msg } from "../../../contract";
import { filterText, labelText, type PhraseFormat } from "./catalog";
import type { Exclusion, GroupLabel, Population, RowMeaning, RunColumn } from "./types";

export interface DescribeFormat extends PhraseFormat {
  plural(n: number, one: string, other: string): string;
}

export function rowMeaningText(m: RowMeaning, f: DescribeFormat): string {
  switch (m.grain) {
    case "animal": return f.t("One row per animal");
    case "event": return m.record ? f.t("One row per record: {{record}}", { record: f.t(m.record) }) : f.t("One row per record");
    case "animal_event": return m.record ? f.t("One row per animal per record: {{record}}", { record: f.t(m.record) }) : f.t("One row per animal per record");
    case "group": return f.t("One row per group");
    case "place": return f.t("One row per place");
    case "lot": return f.t("One row per stock lot");
    case "item": return f.t("One row per product");
    case "task": return f.t("One row per task");
    case "doc": return f.t("One row per financial record");
    case "line": return f.t("One row per line on a financial record");
    case "summary": {
      const g = (m.groups ?? []).map((x) => f.t(x).toLocaleLowerCase());
      return g.length > 1 ? f.t("One row per {{first}} and {{second}}", { first: g[0], second: g[1] }) : f.t("One row per {{group}}", { group: g[0] ?? "" });
    }
  }
}

export function populationText(p: Population, f: DescribeFormat, today?: string): string {
  const n = p.count;
  switch (p.kind) {
    case "animals":
      if (p.scope === "former") return f.plural(n, "{{count}} former animal", "{{count}} former animals");
      if (p.scope === "reference") return f.plural(n, "{{count}} reference animal", "{{count}} reference animals");
      if (p.scope === "all") return f.plural(n, "{{count}} animal, current, former and reference", "{{count}} animals, current, former and reference");
      if (p.as_of && today && p.as_of < today) return f.t(n === 1 ? "{{count}} animal in the herd on {{date}}" : "{{count}} animals in the herd on {{date}}", { count: n, date: f.date(p.as_of) });
      return f.plural(n, "{{count}} animal in the herd", "{{count}} animals in the herd");
    case "records":
      if (p.from && p.to) return f.t(n === 1 ? "{{count}} record from {{from}} to {{to}}" : "{{count}} records from {{from}} to {{to}}", { count: n, from: f.date(p.from), to: f.date(p.to) });
      if (p.to) return f.t(n === 1 ? "{{count}} record up to {{to}}" : "{{count}} records up to {{to}}", { count: n, to: f.date(p.to) });
      return f.plural(n, "{{count}} record", "{{count}} records");
    case "groups": return f.plural(n, "{{count}} group", "{{count}} groups");
    case "places": return f.plural(n, "{{count}} place", "{{count}} places");
    case "lots": return f.plural(n, "{{count}} stock lot", "{{count}} stock lots");
    case "items": return f.plural(n, "{{count}} product", "{{count}} products");
    case "tasks": return f.plural(n, "{{count}} task", "{{count}} tasks");
    case "documents": return f.plural(n, "{{count}} financial record", "{{count}} financial records");
    case "lines": return f.plural(n, "{{count}} line", "{{count}} lines");
  }
}

export function exclusionText(e: Exclusion, f: DescribeFormat): string {
  const parts = [filterText(e.filter, f)];
  if (e.removed) parts.push(f.plural(e.removed, "{{count}} left out", "{{count}} left out"));
  if (e.unknown) parts.push(f.plural(e.unknown, "{{count}} not recorded", "{{count}} not recorded"));
  return parts.join(" · ");
}

/** Notes carry English keys; field labels inside them translate and dates format. */
export function noteText(m: Msg, f: DescribeFormat): string {
  const values: Record<string, unknown> = { ...(m.values ?? {}) };
  if (typeof values.field === "string") values.field = f.t(values.field);
  if (typeof values.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(values.date)) values.date = f.date(values.date);
  return f.t(m.text, values);
}

export function columnHeading(c: RunColumn, f: Pick<DescribeFormat, "t">): string {
  if (c.custom) return c.label;
  if (c.label_field) return f.t(c.label, { field: f.t(c.label_field) });
  return f.t(c.label);
}

export function groupText(labels: GroupLabel[], f: DescribeFormat): string {
  return labels.map((l) => labelText(l, f)).join(" · ");
}

/** "Average: Weaning weight" — the heading for a group total. */
export function totalHeading(fn: string, field: string, f: Pick<DescribeFormat, "t">): string {
  switch (fn) {
    case "avg": return f.t("Average: {{field}}", { field });
    case "sum": return f.t("Total: {{field}}", { field });
    case "min": return f.t("Lowest: {{field}}", { field });
    case "max": return f.t("Highest: {{field}}", { field });
    default: return f.t("Count: {{field}}", { field });
  }
}

/** Keys chosen at runtime above, listed so the catalog check sees them. */
export const DESCRIBE_MESSAGES = [
  "{{count}} animal in the herd on {{date}}", "{{count}} animals in the herd on {{date}}",
  "{{count}} record from {{from}} to {{to}}", "{{count}} records from {{from}} to {{to}}",
  "{{count}} record up to {{to}}", "{{count}} records up to {{to}}",
];
